import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ApiResult<T> = { data?: T; error?: string; message?: string };
type Tenant = { id: string; name: string; slug: string; account_status: string; billing_country: string | null; owner_email: string | null; user_count: number; customer_count: number };
type Overview = { tenants: number; users: number; customers: number; campaigns: { status: string; total: string }[]; wallet_passes: { status: string; total: string }[]; queue: Record<string, number> | null };
export type ControlRole = 'support' | 'ops' | 'billing' | 'security' | 'admin';
type ControlMe = { id: string; subject: string; email: string; displayName: string; roles: ControlRole[] };
type ControlTab = 'overview' | 'billing' | 'users' | 'programs' | 'integrations' | 'jobs' | 'audit';

const tabRoles: Record<ControlTab, ControlRole[]> = {
  overview: ['support', 'billing', 'admin'],
  billing: ['billing', 'admin'],
  users: ['support', 'admin'],
  programs: ['support', 'admin'],
  integrations: ['ops', 'admin'],
  jobs: ['ops', 'admin'],
  audit: ['security', 'admin'],
};

export function canAccessControlTab(roles: ControlRole[], tab: ControlTab) {
  return roles.some((role) => tabRoles[tab].includes(role));
}

export function canStartSupportSession(roles: ControlRole[]) {
  return roles.some((role) => role === 'support' || role === 'security' || role === 'admin');
}

export function defaultControlTab(roles: ControlRole[]): ControlTab {
  const ordered: ControlTab[] = ['overview', 'billing', 'users', 'programs', 'integrations', 'jobs', 'audit'];
  return ordered.find((tab) => canAccessControlTab(roles, tab)) ?? 'overview';
}

const apiBase = (import.meta.env.VITE_CONTROL_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

async function api<T>(path: string, options?: RequestInit, bearer?: string): Promise<T> {
  const response = await fetch(`${apiBase}/v1/control${path}`, { ...options, credentials: 'include', headers: { 'content-type': 'application/json', ...(bearer ? { authorization: `Bearer ${bearer}` } : {}), ...(options?.headers ?? {}) } });
  const payload = await response.json() as ApiResult<T>;
  if (!response.ok) throw new Error(payload.message ?? payload.error ?? `Request failed (${response.status})`);
  return payload.data as T;
}

function Status({ value }: { value: string }) { return <span className={`status status-${value.replace(/[^a-z0-9]/gi, '-')}`}>{value}</span>; }

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function App() {
  const [operator, setOperator] = useState<ControlMe | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<ControlTab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [support, setSupport] = useState<{ id: string; tenant_id: string; expires_at: string; support_token?: string } | null>(null);
  const [endingSupport, setEndingSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({ reason: '', ticket_title: '', duration_minutes: 15, open: false });
  const supportDialogRef = useRef<HTMLDivElement>(null);
  const startSupportButtonRef = useRef<HTMLButtonElement>(null);

  const loadOverview = useCallback(async () => {
    setError(null);
    try {
      const me = await api<ControlMe>('/me');
      setOperator(me);
      const canReadGlobalOverview = me.roles.some((role) => role === 'ops' || role === 'security' || role === 'admin');
      const [summary, list] = await Promise.allSettled([
        canReadGlobalOverview ? api<Overview>('/overview') : Promise.resolve(null),
        api<{ items: Tenant[] }>('/tenants?limit=25'),
      ]);
      if (summary.status === 'fulfilled') setOverview(summary.value);
      else setOverview(null);
      if (list.status === 'fulfilled') setTenants(list.value.items);
      else throw list.reason;
    }
    catch (err) { setError(err instanceof Error ? err.message : 'Falha ao carregar control plane.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    const timer = window.setTimeout(async () => {
      try { const list = await api<{ items: Tenant[] }>(`/tenants?limit=25&search=${encodeURIComponent(search)}`); setTenants(list.items); }
      catch (err) { setError(err instanceof Error ? err.message : 'Falha na busca.'); }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    if (!supportForm.open) return;
    const dialog = supportDialogRef.current;
    if (!dialog) return;
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
    focusable()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSupportForm((value) => ({ ...value, open: false }));
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (startSupportButtonRef.current?.isConnected) startSupportButtonRef.current.focus();
    };
  }, [supportForm.open]);

  const openTenant = (id: string) => { setSelected(id); setTab(defaultControlTab(operator?.roles ?? [])); setError(null); };
  const startSupport = async () => {
    if (!selected || supportForm.reason.trim().length < 10 || !supportForm.ticket_title.trim()) return;
    try {
      const result = await api<{ session: { id: string; tenant_id: string; expires_at: string }; support_token: string }>(`/tenants/${selected}/support-sessions`, { method: 'POST', body: JSON.stringify({ reason: supportForm.reason, ticket_title: supportForm.ticket_title, duration_minutes: supportForm.duration_minutes, scopes: ['read_only'] }) });
      setSupport({ ...result.session, support_token: result.support_token }); setSupportForm({ reason: '', ticket_title: '', duration_minutes: 15, open: false });
    } catch (err) { setError(err instanceof Error ? err.message : 'Falha ao criar sessão.'); }
  };
  const endSupport = async () => {
    if (!support || endingSupport) return;
    setEndingSupport(true); setError(null);
    try {
      // Revoke with the operator's Access session, never with the support JWT.
      await api(`/support-sessions/${support.id}/revoke`, { method: 'POST' });
      setSupport(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Falha ao encerrar sessão.'); }
    finally { setEndingSupport(false); }
  };

  const tabs = useMemo<ControlTab[]>(() => ['overview', 'billing', 'users', 'programs', 'integrations', 'jobs', 'audit'], []);
  const panel = selected ? `/tenants/${selected}/${tab === 'overview' ? 'overview' : tab}` : '';
  const selectedTenant = tenants.find((tenant) => tenant.id === selected);
  const visibleTabs = tabs.filter((name) => canAccessControlTab(operator?.roles ?? [], name));

  return <div className="shell">
    <aside className="sidebar"><div className="brand"><span className="brand-mark">F</span><div><strong>Fideliza</strong><small>CONTROL PLANE</small></div></div><nav aria-label="Control plane navigation"><button type="button" className="nav-active">Overview</button><button type="button">Tenants</button><button type="button">Jobs & webhooks</button><button type="button">Audit log</button></nav><div className="access-note"><span className="dot" />Cloudflare Access<br /><small>MFA enforced · read-only console</small></div></aside>
    <main className="main"><header className="topbar"><div><p className="eyebrow">OPERATIONS / PLATFORM</p><h1>Control plane</h1></div><button type="button" className="refresh" onClick={loadOverview}>Refresh data</button></header>
      {support && <div className="support-banner" role="status" aria-live="polite"><strong>Support session active</strong><span>Tenant {support.tenant_id.slice(0, 8)} · expires {new Date(support.expires_at).toLocaleTimeString()}</span><button type="button" disabled={endingSupport} onClick={endSupport}>{endingSupport ? 'Encerrando…' : 'Encerrar sessão'}</button></div>}
      {error && <div className="error" role="alert">{error}<button type="button" aria-label="Fechar mensagem de erro" onClick={() => setError(null)}>×</button></div>}
      {loading ? <div className="loading" role="status" aria-live="polite">Loading control plane…</div> : <>
        {overview ? <section className="metrics" aria-label="Platform metrics">{[['Tenants', overview.tenants], ['Users', overview.users], ['Customers', overview.customers], ['Queue active', overview.queue?.active ?? 0]].map(([label, value]) => <div className="metric" key={String(label)}><span>{label}</span><strong>{value}</strong><small>live</small></div>)}</section> : <div className="empty">Global overview is not available for this operator role.</div>}
        <section className="workspace"><div className="panel tenant-list"><div className="panel-head"><div><p className="eyebrow">DIRECTORY</p><h2>Tenants</h2></div><span className="count">{tenants.length}</span></div><label className="sr-only" htmlFor="tenant-search">Buscar tenants</label><input id="tenant-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name, slug, owner or ID" />{tenants.map((tenant) => <button type="button" className={`tenant-row ${selected === tenant.id ? 'selected' : ''}`} key={tenant.id} onClick={() => openTenant(tenant.id)}><span className="tenant-avatar" aria-hidden="true">{tenant.name.slice(0, 1).toUpperCase()}</span><span className="tenant-info"><strong>{tenant.name}</strong><small>{tenant.slug} · {tenant.owner_email ?? 'owner hidden'}</small></span><Status value={tenant.account_status} /></button>)}</div>
          <div className="panel detail"><div className="panel-head"><div><p className="eyebrow">TENANT DETAIL</p><h2>{selectedTenant?.name ?? 'Select a tenant'}</h2></div>{selected && !support && canStartSupportSession(operator?.roles ?? []) && <button type="button" ref={startSupportButtonRef} className="support-btn" onClick={() => setSupportForm((value) => ({ ...value, open: true }))}>Start support session</button>}</div>{selected && <div className="tabs" role="tablist" aria-label="Tenant detail sections">{visibleTabs.map((name) => <button type="button" role="tab" aria-selected={tab === name} className={tab === name ? 'active' : ''} key={name} onClick={() => setTab(name)}>{name}</button>)}</div>}{selected ? <div role="tabpanel" aria-label={`${tab} details`}><DetailPanel tab={tab} path={panel} bearer={support?.tenant_id === selected ? support.support_token : undefined} /></div> : <div className="empty">Choose a tenant to inspect platform state.</div>}</div></section>
      </>}
    </main>
    {supportForm.open && <div className="modal-backdrop" role="presentation"><div className="modal" ref={supportDialogRef} role="dialog" aria-modal="true" aria-labelledby="support-dialog-title" aria-describedby="support-dialog-description"><p className="eyebrow">TIME-BOXED ACCESS</p><h2 id="support-dialog-title">Start support session</h2><p id="support-dialog-description" className="muted">Default scope is read-only. Every access is logged and the session can be revoked immediately.</p><label htmlFor="ticket-title">Ticket title<input id="ticket-title" value={supportForm.ticket_title} onChange={(event) => setSupportForm({ ...supportForm, ticket_title: event.target.value })} /></label><label htmlFor="support-reason">Reason<textarea id="support-reason" value={supportForm.reason} onChange={(event) => setSupportForm({ ...supportForm, reason: event.target.value })} /></label><label htmlFor="support-duration">Duration (minutes)<input id="support-duration" type="number" min="1" max="30" value={supportForm.duration_minutes} onChange={(event) => setSupportForm({ ...supportForm, duration_minutes: Number(event.target.value) })} /></label><div className="modal-actions"><button type="button" onClick={() => setSupportForm({ ...supportForm, open: false })}>Cancel</button><button type="button" className="primary" onClick={startSupport}>Create session</button></div></div></div>}
  </div>;
}

function DetailPanel({ tab, path, bearer }: { tab: ControlTab; path: string; bearer?: string }) {
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setPayload(null); setError(null); api<Record<string, unknown>>(path, undefined, bearer).then(setPayload).catch((err) => setError(err instanceof Error ? err.message : 'Falha ao carregar detalhe.')); }, [bearer, path]);
  if (error) return <div className="empty error-inline">{error}</div>;
  if (!payload) return <div className="empty">Loading detail…</div>;
  if (tab === 'overview' && payload.tenant && payload.counts) {
    const counts = payload.counts as Record<string, unknown>;
    return <div className="detail-grid">{Object.entries(counts).map(([key, value]) => <div className="detail-stat" key={key}><small>{key.replaceAll('_', ' ')}</small><strong>{String(value)}</strong></div>)}<pre>{JSON.stringify(payload.tenant, null, 2)}</pre></div>;
  }
  return <pre className="json-view">{JSON.stringify(payload, null, 2)}</pre>;
}
